/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../theme/theme"
import { css } from "@mui/material"
import { ReactNode } from "react"
import SwipeableViews from "react-swipeable-views"
import { IconButton } from "@mui/material"
import { ArrowBack, ArrowBackIos, ArrowForward, ArrowForwardIos } from "@mui/icons-material"
import { mobileCss } from "../util/isMobile"

interface ViewPagerProps {
    currentIndex: number
    onIndexChange: (index: number) => void
    children: ReactNode[]
    showNavigation?: boolean
}

const NavigationContainerStyle = withMyTheme((theme) => css`
    width: 100vw;
`)

const ArrowButtonStyle = withMyTheme((theme) => css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-color: ${theme.palette.primary.main};
    color: white;
    width: 48px;
    height: 48px;
    z-index: 10;
    
    &:hover {
        background-color: ${theme.palette.primary.dark};
    }
    
    &:disabled {
        background-color: ${theme.palette.grey[300]};
        color: ${theme.palette.grey[500]};
    }
    
    .MuiSvgIcon-root {
        font-size: 1.2rem;
    }
`)

const LeftArrowStyle = withMyTheme((theme) => css`
    left: 20px;
    
    ${mobileCss(`
        left: 16px;
    `)}
`)

const RightArrowStyle = withMyTheme((theme) => css`
    right: 20px;
    
    ${mobileCss(`
        right: 16px;
    `)}
`)

const ViewPagerContainerStyle = withMyTheme((theme) => css`
    position: relative;
    width: 100vw;
`)

const StepContainerStyle = withMyTheme((theme) => css`
    padding: 3rem 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    
    ${mobileCss(`
        padding: 0rem 1rem;
        min-height: 300px;
    `)}
`)

const SwipeableViewsContainerStyle = withMyTheme((theme) => css`
    width: 100vw;
    padding-top: 2vh;
`)

export const ViewPager = ({ 
    currentIndex, 
    onIndexChange, 
    children, 
    showNavigation = true 
}: ViewPagerProps) => {
    const totalPages = children.length
    
    const handlePrevious = () => {
        if (currentIndex > 0) {
            onIndexChange(currentIndex - 1)
        }
    }
    
    const handleNext = () => {
        if (currentIndex < totalPages - 1) {
            onIndexChange(currentIndex + 1)
        }
    }
    
    return (
        <div css={NavigationContainerStyle}>
            <div css={ViewPagerContainerStyle}>
                <SwipeableViews
                    css={SwipeableViewsContainerStyle}
                    index={currentIndex}
                    onChangeIndex={onIndexChange}
                    enableMouseEvents
                    resistance
                >
                    {children.map((child, index) => (
                        <div key={index} css={StepContainerStyle}>
                            {child}
                        </div>
                    ))}
                </SwipeableViews>
                
                {showNavigation && (
                    <>
                        <IconButton
                            css={[ArrowButtonStyle, LeftArrowStyle]}
                            onClick={handlePrevious}
                            disabled={currentIndex === 0}
                        >
                            <ArrowBack />
                        </IconButton>
                        
                        <IconButton
                            css={[ArrowButtonStyle, RightArrowStyle]}
                            onClick={handleNext}
                            disabled={currentIndex === totalPages - 1}
                        >
                            <ArrowForward />
                        </IconButton>
                    </>
                )}
            </div>
        </div>
    )
}